import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Pencil, Instagram } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import WhatsAppButton from "@/components/WhatsAppButton";

export default function Suppliers() {
  const { apiCall } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", contact_name: "", phone: "", email: "", instagram_handle: "", notes: "", category: "" });

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiCall("get", "/suppliers");
      setSuppliers(res.data);
    } catch {
      toast.error("Erro ao carregar fornecedores");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const handleOpen = (s = null) => {
    setEditing(s);
    setForm(s ? {
      name: s.name || "",
      contact_name: s.contact_name || "",
      phone: s.phone || "",
      email: s.email || "",
      instagram_handle: s.instagram_handle || "",
      notes: s.notes || "",
      category: s.category || "",
    } : { name: "", contact_name: "", phone: "", email: "", instagram_handle: "", notes: "", category: "" });
    setOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await apiCall("put", `/suppliers/${editing.supplier_id}`, form);
        toast.success("Fornecedor atualizado");
      } else {
        await apiCall("post", "/suppliers", form);
        toast.success("Fornecedor cadastrado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este fornecedor?")) return;
    try {
      await apiCall("delete", `/suppliers/${id}`);
      toast.success("Excluído");
      load();
    } catch {
      toast.error("Erro");
    }
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <PageHeader overline="Cadastros" title="Fornecedores" subtitle="Contatos para reposição de produtos e materiais">
        <Button onClick={() => handleOpen(null)} data-testid="add-supplier-button">
          <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> Novo Fornecedor
        </Button>
      </PageHeader>

      <Card className="border border-border bg-card shadow-soft rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/30 hover:bg-secondary/30">
              <TableHead className="text-xs uppercase tracking-widest font-medium">Nome</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Contato</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Categoria</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Telefone</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">@instagram</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest font-medium">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : suppliers.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">Nenhum fornecedor cadastrado.</TableCell></TableRow>
            ) : (
              suppliers.map((s) => (
                <TableRow key={s.supplier_id} data-testid={`supplier-row-${s.supplier_id}`}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{s.contact_name || "—"}</TableCell>
                  <TableCell className="text-sm">{s.category || "—"}</TableCell>
                  <TableCell className="text-sm">{s.phone || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {s.instagram_handle ? (
                      <a href={`https://instagram.com/${s.instagram_handle.replace("@", "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-pink-600 hover:underline">
                        <Instagram className="h-3 w-3" strokeWidth={1.5} /> {s.instagram_handle}
                      </a>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {s.phone && (
                      <WhatsAppButton
                        toPhone={s.phone}
                        message={`Olá ${s.contact_name || s.name}, tudo bem? Aqui é do salão Gregory João.`}
                        testId={`whatsapp-supplier-${s.supplier_id}`}
                      />
                    )}
                    <Button variant="ghost" size="icon" onClick={() => handleOpen(s)} data-testid={`edit-supplier-${s.supplier_id}`}>
                      <Pencil className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(s.supplier_id)} data-testid={`delete-supplier-${s.supplier_id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">{editing ? "Editar Fornecedor" : "Novo Fornecedor"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest">Nome da empresa</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="supplier-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Contato (pessoa)</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="supplier-contact-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Telefone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+55 11 99999-9999" data-testid="supplier-phone-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">@instagram</Label>
                <Input value={form.instagram_handle} onChange={(e) => setForm({ ...form, instagram_handle: e.target.value })} placeholder="@fornecedor" data-testid="supplier-ig-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="supplier-email-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Categoria</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ex: Cabelo, Unhas, Materiais" data-testid="supplier-category-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Anotações</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Condições, prazo de entrega, etc." data-testid="supplier-notes-input" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="supplier-save-button">{editing ? "Atualizar" : "Cadastrar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
